import React, { useState, useEffect } from "react";
import {
  Typography,
  IconButton,
  Card,
  CardContent,
  FormControlLabel,
  Collapse,
  Button,
} from "@mhome/ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import { platformState, activeScopeState } from "../global-state";
import { useToast } from "../common/toast";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoadingSpinner from "../common/loading";
import {
  useIsSmallScreen,
  useComponentBackgroundColor,
} from "../common/adaptive-theme-provider";
import MSwitch from "../common/switch";
import { useAppInterface } from "../app/app-interface-provider";
import { spacingToPx } from "../lib/utils";

const NOTIFICATION_SETTINGS_KEY = "notification_settings";

const getNotificationSettings = () => {
  const saved = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);
  return saved
    ? JSON.parse(saved)
    : {
        desktopNotificationEnabled: false,
        notificationPermission: "default",
      };
};

const saveNotificationSettings = (settings) => {
  localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(settings));
};

const DesktopSetting = () => {
  const history = useHistory();
  const [platform] = useRecoilState(platformState);
  const { successToast, warningToast, errorToast } = useToast();
  const appInterface = useAppInterface();
  const isDesktop = platform?.source === "desktop";
  const isSmallScreen = useIsSmallScreen();
  const [activeScope] = useRecoilState(activeScopeState);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [notificationExpanded, setNotificationExpanded] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState(
    getNotificationSettings()
  );

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  const fetchDeviceInfo = async () => {
    if (!appInterface.isAvailable()) {
      errorToast("Desktop interface not available");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const [info, categories] = await Promise.all([
        await appInterface.getDesktopInfo(activeScope),
        await appInterface.getDesktopCategories(),
      ]);
      console.debug("info", info);
      console.debug("categories", categories);
      setDeviceInfo(info || null);
      setAllCategories(categories || []);
    } catch (error) {
      console.error("Error fetching desktop info:", error);
      errorToast("Error fetching desktop info");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMainToggle = async (enabled) => {
    const scopeId = activeScope;
    try {
      const resp = await appInterface.enableDisableDesktop(scopeId, enabled);
      if (resp) {
        await fetchDeviceInfo();
        successToast(enabled ? "Desktop enabled" : "Desktop disabled");
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      console.error("Error toggling desktop:", error);
      errorToast("Error!");
    }
  };

  const handleCategoryToggle = async (categoryId, enabled) => {
    const scopeId = activeScope;
    try {
      const resp = await appInterface.setDesktopDataCategory(
        scopeId,
        categoryId,
        enabled
      );
      if (resp) {
        await fetchDeviceInfo();
        const categoryName =
          allCategories.find((cat) => cat.id === categoryId)?.displayName ||
          categoryId;
        successToast(`${categoryName} ${enabled ? "enabled" : "disabled"}`);
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      console.error("Error toggling category:", error);
      errorToast("Error!");
    }
  };

  const handleCapabilityToggle = async (capabilityId, enabled) => {
    const scopeId = activeScope;
    try {
      const resp = await appInterface.setDesktopCapability(
        scopeId,
        capabilityId,
        enabled
      );
      if (resp) {
        await fetchDeviceInfo();
        successToast(`${capabilityId} ${enabled ? "enabled" : "disabled"}`);
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      console.error("Error toggling capability:", error);
      errorToast("Error!");
    }
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const isCategoryEnabled = (categoryId) => {
    return deviceInfo?.categories?.includes(categoryId) || false;
  };

  const isCapabilityEnabled = (capabilityId) => {
    return deviceInfo?.supportedCapabilities?.includes(capabilityId) || false;
  };

  const handleNotificationChange = async (event) => {
    const enabled = event.target.checked;

    // For desktop platforms (Tauri), use native notification permission check
    if (platform.source === "desktop") {
      if (enabled) {
        try {
          const permission = await appInterface.checkNotificationPermission();
          if (permission) {
            setNotificationSettings((prev) => ({
              ...prev,
              desktopNotificationEnabled: enabled,
              notificationPermission: "granted",
            }));
            saveNotificationSettings({
              ...notificationSettings,
              desktopNotificationEnabled: enabled,
              notificationPermission: "granted",
            });
            successToast("Notifications enabled successfully");
          } else {
            setNotificationSettings((prev) => ({
              ...prev,
              desktopNotificationEnabled: false,
              notificationPermission: "denied",
            }));
            saveNotificationSettings({
              ...notificationSettings,
              desktopNotificationEnabled: false,
              notificationPermission: "denied",
            });
            warningToast("Notification permission was not granted");
          }
        } catch (error) {
          console.error("Error checking notification permission:", error);
          errorToast("Failed to check notification permission");
        }
      } else {
        setNotificationSettings((prev) => ({
          ...prev,
          desktopNotificationEnabled: enabled,
        }));
        saveNotificationSettings({
          ...notificationSettings,
          desktopNotificationEnabled: enabled,
        });
        successToast("Notifications disabled");
      }
    } else {
      setNotificationSettings((prev) => ({
        ...prev,
        desktopNotificationEnabled: enabled,
      }));
      saveNotificationSettings({
        ...notificationSettings,
        desktopNotificationEnabled: enabled,
      });
      if (enabled) {
        successToast("Notifications enabled successfully");
      } else {
        successToast("Notifications disabled");
      }
    }
  };

  const sendTestNotification = () => {
    // Use the same structure as actual notification payload
    const notification = {
      title: "Test Notification",
      message: "This is a test notification",
      priority: "low",
      timestamp: Date.now(),
    };

    // Use Tauri native notification API - pass the entire notification object
    if (platform.source === "desktop") {
      try {
        appInterface.sendNotification(notification);
        successToast("Test notification sent");
      } catch (e) {
        console.error("Failed to send notification:", e);
        errorToast("Failed to send notification");
      }
    } else {
      errorToast("Notification permission not granted");
    }
  };

  useEffect(() => {
    if (!isDesktop) {
      // Mock data for web display
      setDeviceInfo({
        status: "active",
        deviceName: "Web Demo Device",
        categories: ["fileSystem", "network"],
        supportedCapabilities: ["readFile", "writeFile"],
      });
      setAllCategories([
        {
          id: "fileSystem",
          displayName: "File System",
          capabilities: [
            {
              capability: "readFile",
              name: "Read Files",
              description: "Allow reading files from your desktop",
            },
            {
              capability: "writeFile",
              name: "Write Files",
              description: "Allow writing files to your desktop",
            },
          ],
        },
        {
          id: "network",
          displayName: "Network",
          capabilities: [],
        },
      ]);
      setIsLoading(false);
    } else if (isDesktop) {
      fetchDeviceInfo();
    }
  }, [isDesktop]);

  useEffect(() => {
    const checkNotificationPermission = async () => {
      // For desktop platforms (Tauri), use native notification permission check
      if (platform.source === "desktop") {
        try {
          const permission = await appInterface.checkNotificationPermission();
          const permissionStatus = permission ? "granted" : "denied";
          setNotificationSettings((prev) => ({
            ...prev,
            notificationPermission: permissionStatus,
          }));
          saveNotificationSettings({
            ...notificationSettings,
            notificationPermission: permissionStatus,
          });
        } catch (error) {
          console.error("Error checking notification permission:", error);
        }
      }
    };

    if (notificationSettings.desktopNotificationEnabled) {
      checkNotificationPermission();
    }
  }, [notificationSettings.desktopNotificationEnabled]);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          padding: spacingToPx(2),
          width: "100%",
        }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  const isActive = deviceInfo?.status === "active";
  const deviceName = deviceInfo?.deviceName || "This Device";

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        flexDirection: "column",
        height: "100%",
        padding: spacingToPx(2),
        width: "100%",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        {/* Main Device Card */}
        <Card
          style={{
            width: "100%",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            boxShadow: "none",
          }}
        >
          <CardContent>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body1" color="primary">
                  {deviceName}
                </Typography>
              </div>
              <FormControlLabel
                control={
                  <MSwitch
                    checked={isActive}
                    onChange={(e) => handleMainToggle(e.target.checked)}
                  />
                }
                label=""
              />
            </div>
          </CardContent>
        </Card>

        {isActive && (
          <Card
            style={{
              width: "100%",
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
              boxShadow: "none",
              marginTop: spacingToPx(2),
            }}
          >
            <CardContent>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setNotificationExpanded(!notificationExpanded)
                    }
                    style={{
                      marginRight: spacingToPx(1),
                      width: "32px",
                      height: "32px",
                    }}
                  >
                    {notificationExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                  <div style={{ flex: 1 }}>
                    <Typography variant="body1" color="primary">
                      Pop-up Notifications
                    </Typography>
                    <Typography
                      variant="caption"
                      className="text-muted-foreground"
                      style={{
                        display: "block",
                        marginTop: spacingToPx(0.5),
                      }}
                    >
                      Enable notifications to receive important alerts from the
                      application.
                    </Typography>
                  </div>
                </div>
                <MSwitch
                  checked={notificationSettings.desktopNotificationEnabled}
                  onChange={handleNotificationChange}
                  disabled={
                    notificationSettings.notificationPermission === "denied"
                  }
                />
              </div>
              {notificationSettings.desktopNotificationEnabled && (
                <Collapse in={notificationExpanded}>
                  <div
                    style={{
                      marginTop: spacingToPx(2),
                      paddingLeft: spacingToPx(5),
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={sendTestNotification}
                      style={{
                        textTransform: "none",
                      }}
                      size="small"
                    >
                      Send Test Notification
                    </Button>
                  </div>
                </Collapse>
              )}
            </CardContent>
          </Card>
        )}

        {/* Categories Section */}
        {allCategories.length > 0 && (
          <>
            <Typography
              variant="subtitle1"
              style={{
                fontWeight: 500,
                marginBottom: spacingToPx(1),
                marginTop: spacingToPx(2),
              }}
            >
              Data Categories
            </Typography>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: spacingToPx(1),
                marginBottom: spacingToPx(2),
              }}
            >
              {allCategories.map((category) => {
                const categoryEnabled = isCategoryEnabled(category.id);
                const isExpanded = expandedCategories[category.id];
                const hasCapabilities =
                  category.capabilities && category.capabilities.length > 0;

                return (
                  <Card
                    key={category.id}
                    style={{
                      width: "100%",
                      backgroundColor: getComponentBgColor("hsl(var(--card))"),
                      boxShadow: "none",
                      opacity: isActive ? 1 : 0.6,
                      transition: "none",
                    }}
                  >
                    <CardContent style={{ padding: spacingToPx(1.5) }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                          minHeight: "48px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          {hasCapabilities && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                toggleCategoryExpansion(category.id)
                              }
                              style={{
                                marginRight: spacingToPx(1),
                                width: "32px",
                                height: "32px",
                              }}
                            >
                              {isExpanded ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          )}
                          {!hasCapabilities && (
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                marginRight: spacingToPx(1),
                              }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              className="text-foreground"
                              style={{
                                fontWeight: 500,
                              }}
                            >
                              {category.displayName}
                            </Typography>
                          </div>
                        </div>
                        <FormControlLabel
                          control={
                            <MSwitch
                              size="small"
                              checked={categoryEnabled}
                              onChange={(e) =>
                                handleCategoryToggle(
                                  category.id,
                                  e.target.checked
                                )
                              }
                              disabled={!isActive}
                            />
                          }
                          label=""
                          onClick={(event) => event.stopPropagation()}
                        />
                      </div>

                      {/* Capabilities */}
                      {hasCapabilities && (
                        <Collapse in={isExpanded}>
                          <div
                            style={{
                              marginTop: spacingToPx(1),
                              paddingLeft: spacingToPx(5),
                            }}
                          >
                            {category.capabilities.map((capabilityObj) => {
                              const capabilityEnabled = isCapabilityEnabled(
                                capabilityObj.capability
                              );

                              return (
                                <div
                                  key={capabilityObj.capability}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    minHeight: "48px",
                                    paddingTop: spacingToPx(1),
                                    paddingBottom: spacingToPx(1),
                                  }}
                                >
                                  <div style={{ flex: 1 }}>
                                    <Typography
                                      variant="body2"
                                      className="text-foreground"
                                      style={{
                                        fontWeight: 500,
                                      }}
                                    >
                                      {capabilityObj.name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      className="text-muted-foreground"
                                    >
                                      {capabilityObj.description}
                                    </Typography>
                                  </div>
                                  <FormControlLabel
                                    control={
                                      <MSwitch
                                        size="small"
                                        checked={capabilityEnabled}
                                        onChange={(e) =>
                                          handleCapabilityToggle(
                                            capabilityObj.capability,
                                            e.target.checked
                                          )
                                        }
                                        disabled={!isActive || !categoryEnabled}
                                      />
                                    }
                                    label=""
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </Collapse>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {/* Info Card */}
        <Card
          style={{
            width: "100%",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            boxShadow: "none",
            marginTop: spacingToPx(2),
          }}
        >
          <CardContent>
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <InfoOutlinedIcon
                style={{
                  color: "hsl(var(--primary))",
                  marginRight: spacingToPx(2),
                }}
              />
              <div>
                <Typography variant="body2" color="text.secondary">
                  {deviceInfo ? (
                    <>
                      Attention! By enabling desktop permissions, you are
                      exposing this desktop to the current space. Choose the
                      permission categories to control the specific data to be
                      shared with mHome service and the space.
                      <br />
                      For example, by just enabling the desktop device, users in
                      the space will know this device exists, but no real data
                      is exposed.
                      <br />
                      But if you enabled the &quot;File System&quot; category,
                      automations can operate your local files, which also means
                      other users in the space have the access to your files.
                    </>
                  ) : (
                    <>
                      Enable desktop permissions to allow mHome service to
                      access your desktop, including reading data and make
                      operations. You will be able to control the access for
                      each data category.
                      <br />
                      All the users within the current space will be able to
                      access this desktop on data categories you enabled.
                    </>
                  )}
                </Typography>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DesktopSetting;
